import { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import StatCard from "@/components/StatCard";
import { Clock, TrendingUp, Palmtree, CheckCircle2, FileDown, FileText, FileSpreadsheet, MoreHorizontal, Coffee } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";

const EXPORT_ROLES = ["super_admin", "admin", "hr_user", "manager"];
const PAY_RATE_ROLES = ["super_admin", "admin", "hr_user"];

async function downloadAs(url, filename) {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    const text = await res.text();
    try { const j = JSON.parse(text); throw new Error(j.detail || "Export failed"); }
    catch { throw new Error(text || "Export failed"); }
  }
  const blob = await res.blob();
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

export default function ReportsPage() {
  const { user } = useAuth();
  const canExport = EXPORT_ROLES.includes(user?.role);
  const canSeePay = PAY_RATE_ROLES.includes(user?.role);

  const [scope, setScope] = useState("week");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [department, setDepartment] = useState("all");
  const [employeeId, setEmployeeId] = useState("all");
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [includePps, setIncludePps] = useState(false);
  const [includePayRates, setIncludePayRates] = useState(false);
  const [signPdf, setSignPdf] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const e = await api.get("/employees");
        setEmployees(e.data);
        const d = await api.get("/employees/meta/departments");
        setDepartments(d.data);
      } catch { /* ignore */ }
    })();
  }, []);

  const buildParams = () => {
    const params = new URLSearchParams();
    params.set("scope", scope);
    if (scope === "month") { params.set("year", year); params.set("month", month); }
    if (scope === "year") { params.set("year", year); }
    if (department !== "all") params.set("department", department);
    if (employeeId !== "all") params.set("employee_id", employeeId);
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(buildParams());
      const { data } = await api.get("/reports/summary", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportReport = async (format) => {
    setExporting(format);
    try {
      const params = buildParams();
      if (format === "pdf" && signPdf) params.set("signed", "true");
      const url = `${API}/exports/report/${format}?${params.toString()}`;
      const today = new Date().toISOString().slice(0, 10);
      const ext = format === "pdf" ? "pdf" : "csv";
      const fname = format === "pdf" && signPdf ? `hr-report-signed-${today}.pdf` : `hr-report-${scope}-${today}.${ext}`;
      await downloadAs(url, fname);
      toast.success(`${format.toUpperCase()} export ready${format === "pdf" && signPdf ? " (signed)" : ""}`);
    } catch (e) { toast.error(e.message || "Export failed"); }
    finally { setExporting(null); }
  };

  const exportPayrollPrep = async () => {
    setExporting("payroll");
    try {
      const params = buildParams();
      if (includePps) params.set("include_pps", "true");
      if (includePayRates) params.set("include_pay_rates", "true");
      const url = `${API}/exports/payroll-prep/csv?${params.toString()}`;
      const today = new Date().toISOString().slice(0, 10);
      await downloadAs(url, `payroll-prep-${today}.csv`);
      toast.success("Payroll Prep CSV ready");
    } catch (e) { toast.error(e.message || "Export failed"); }
    finally { setExporting(null); }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="tiny-label">Operations analytics</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Reports</h1>
          <p className="text-muted-foreground mt-1">Weekly, monthly, and annual summaries. Export to CSV, PDF, or Payroll Prep.</p>
        </div>
        {canExport && (
          <div className="hidden md:flex items-center gap-2" data-testid="export-toolbar">
            <Button variant="outline" onClick={() => exportReport("csv")} disabled={!!exporting} data-testid="export-csv-btn">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> {exporting === "csv" ? "Exporting…" : "CSV"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="export-pdf-menu" disabled={!!exporting}>
                  <FileText className="w-4 h-4 mr-1" /> {exporting === "pdf" ? "Exporting…" : "PDF"}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>PDF export</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="p-3 space-y-3">
                  <label className="flex items-start gap-3 text-sm cursor-pointer">
                    <Switch checked={signPdf} onCheckedChange={setSignPdf} data-testid="sign-pdf-toggle" />
                    <div>
                      <div className="font-medium leading-tight">Add signed report integrity stamp</div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Adds an internal SHA-256 integrity stamp to the PDF and audit log. Tamper-evident; not a legal digital signature.
                      </div>
                    </div>
                  </label>
                  <Button onClick={() => exportReport("pdf")} disabled={!!exporting} className="w-full" data-testid="export-pdf-btn">
                    {exporting === "pdf" ? "Exporting…" : (signPdf ? "Download signed PDF" : "Download PDF")}
                  </Button>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="payroll-prep-menu"><FileDown className="w-4 h-4 mr-1" /> Payroll Prep</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Payroll Prep CSV</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="p-3 space-y-3 text-xs text-muted-foreground">
                  <div className="flex items-center justify-between gap-3">
                    <div>Include PPS number column</div>
                    <Switch checked={includePps} onCheckedChange={setIncludePps} data-testid="payroll-include-pps" />
                  </div>
                  {canSeePay && (
                    <div className="flex items-center justify-between gap-3">
                      <div>Include pay rate columns</div>
                      <Switch checked={includePayRates} onCheckedChange={setIncludePayRates} data-testid="payroll-include-rates" />
                    </div>
                  )}
                  <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                    This export is for payroll preparation only and should be reviewed before submission. Not an official Revenue/ROS filing.
                  </div>
                  <Button onClick={exportPayrollPrep} disabled={!!exporting} className="w-full" data-testid="export-payroll-prep-btn">
                    {exporting === "payroll" ? "Exporting…" : "Download Payroll Prep CSV"}
                  </Button>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        {canExport && (
          <div className="md:hidden" data-testid="export-toolbar-mobile">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="mobile-export-menu"><MoreHorizontal className="w-4 h-4 mr-1" /> Export</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportReport("csv")} data-testid="m-export-csv">CSV report</DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportReport("pdf")} data-testid="m-export-pdf">PDF report</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={exportPayrollPrep} data-testid="m-export-payroll">Payroll Prep CSV</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <Card className="border-border shadow-none">
        <CardContent className="p-4 grid sm:grid-cols-6 gap-3 items-end">
          <div>
            <Label>Scope</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="rep-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Current week</SelectItem>
                <SelectItem value="month">Month</SelectItem>
                <SelectItem value="year">Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Year</Label>
            <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
          </div>
          {scope === "month" && (
            <div>
              <Label>Month</Label>
              <Input type="number" min="1" max="12" value={month} onChange={(e) => setMonth(Number(e.target.value))} />
            </div>
          )}
          <div>
            <Label>Department</Label>
            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Employee</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={load} disabled={loading} data-testid="rep-run">{loading ? "Running…" : "Run report"}</Button>
        </CardContent>
      </Card>

      {canExport && (
        <AttendancePdfCard departments={departments} />
      )}

      {!canExport && (
        <Alert className="border-blue-200 bg-blue-50 text-blue-800">
          <AlertDescription>Company reports are available to HR, Manager, Admin and Super Admin roles. You can export your own hours from the "My hours" screen.</AlertDescription>
        </Alert>
      )}

      {data && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard testId="rep-total-hours" label="Total hours" value={data.totals.total_hours.toFixed(2)} sub={`${data.start} → ${data.end}`} icon={Clock} />
            <StatCard testId="rep-overtime" label="Overtime hours" value={data.totals.overtime_hours.toFixed(2)} icon={TrendingUp} tone={data.totals.overtime_hours > 0 ? "warning" : "default"} />
            <StatCard testId="rep-holidays" label="Holiday days" value={data.totals.holiday_taken.toFixed(2)} icon={Palmtree} />
            <StatCard testId="rep-dayoffs" label="Day Off days" value={(data.totals.day_off_taken ?? 0).toFixed(2)} icon={Coffee} />
            <StatCard testId="rep-pending" label="Pending approvals" value={data.totals.pending} sub={`${data.totals.approved} approved`} icon={CheckCircle2} />
          </div>

          <Card className="border-border shadow-none">
            <div className="overflow-x-auto app-scroll">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-left tiny-label">
                    <th className="px-4 py-3">Employee</th>
                    <th className="px-4 py-3">Department</th>
                    <th className="px-4 py-3 text-right">Days</th>
                    <th className="px-4 py-3 text-right">Total hrs</th>
                    <th className="px-4 py-3 text-right">Overtime</th>
                    <th className="px-4 py-3 text-right">Holiday</th>
                    <th className="px-4 py-3 text-right">Remaining</th>
                    <th className="px-4 py-3 text-right">Day Off</th>
                    <th className="px-4 py-3 text-right">Day Off left</th>
                    <th className="px-4 py-3 text-right">Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.employee.id} className="border-t border-border" data-testid={`rep-row-${r.employee.employee_id}`}>
                      <td className="px-4 py-3">{r.employee.full_name}</td>
                      <td className="px-4 py-3">{r.employee.department || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono">{r.days_worked}</td>
                      <td className="px-4 py-3 text-right font-mono font-semibold">{r.total_hours.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-mono">{r.overtime_hours.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-mono">{r.holiday_taken.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-mono">{r.holiday_remaining.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-mono">{(r.day_off_taken ?? 0).toFixed(2)}</td>
                      <td className={`px-4 py-3 text-right font-mono ${(r.day_off_remaining ?? 0) < 0 ? "text-red-600 font-semibold" : ""}`}>{(r.day_off_remaining ?? 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-mono">{r.pending}</td>
                    </tr>
                  ))}
                  {data.rows.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">No data in range.</td></tr>}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}


// ---- Attendance PDF card (weekly + monthly) ----
function AttendancePdfCard({ departments }) {
  const today = new Date();
  // Default = Monday of current week
  const monOf = (d) => {
    const x = new Date(d);
    const diff = (x.getDay() + 6) % 7;
    x.setDate(x.getDate() - diff);
    return x.toISOString().slice(0, 10);
  };
  const [weekStart, setWeekStart] = useState(monOf(today));
  const [monthYear, setMonthYear] = useState(today.getFullYear());
  const [monthN, setMonthN] = useState(today.getMonth() + 1);
  const [dept, setDept] = useState("all");
  const [busy, setBusy] = useState(null);

  const downloadPdf = async (kind) => {
    setBusy(kind);
    try {
      const params = new URLSearchParams();
      if (dept !== "all") params.set("department", dept);
      let url;
      let filename;
      if (kind === "weekly") {
        params.set("week_start", weekStart);
        url = `${API}/reports/weekly-attendance.pdf?${params.toString()}`;
        filename = `weekly-attendance_${weekStart}.pdf`;
      } else {
        params.set("year", monthYear);
        params.set("month", monthN);
        url = `${API}/reports/monthly-attendance.pdf?${params.toString()}`;
        filename = `monthly-attendance_${monthYear}-${String(monthN).padStart(2, "0")}.pdf`;
      }
      await downloadAs(url, filename);
      toast.success(`${kind === "weekly" ? "Weekly" : "Monthly"} PDF ready`);
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setBusy(null);
    }
  };

  const shiftWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(d.toISOString().slice(0, 10));
  };

  const wsDate = new Date(weekStart);
  const weDate = new Date(weekStart); weDate.setDate(wsDate.getDate() + 6);
  const wkLabel = `${wsDate.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} → ${weDate.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <Card className="border-border shadow-none" data-testid="attendance-pdf-card">
      <CardContent className="p-4 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Attendance PDF reports</div>
          <h3 className="text-base font-semibold">Print or email — paginated, text-wrapped, department-grouped</h3>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Weekly */}
          <div className="p-3 border border-border rounded-md space-y-2 bg-muted/20">
            <div className="text-sm font-medium">Weekly PDF</div>
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shiftWeek(-1)} data-testid="att-pdf-week-prev">‹</Button>
              <Input type="date" value={weekStart} onChange={(e) => setWeekStart(monOf(e.target.value))} className="h-9 max-w-[170px]" data-testid="att-pdf-week-start" />
              <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shiftWeek(1)} data-testid="att-pdf-week-next">›</Button>
              <Button variant="outline" size="sm" className="h-9" onClick={() => setWeekStart(monOf(new Date()))}>This week</Button>
            </div>
            <div className="text-xs text-muted-foreground">Week: <b>{wkLabel}</b></div>
            <Button className="w-full h-10" onClick={() => downloadPdf("weekly")} disabled={busy === "weekly"} data-testid="att-pdf-weekly-download">
              <FileDown className="w-4 h-4 mr-2" /> {busy === "weekly" ? "Generating…" : "Download weekly PDF"}
            </Button>
          </div>

          {/* Monthly */}
          <div className="p-3 border border-border rounded-md space-y-2 bg-muted/20">
            <div className="text-sm font-medium">Monthly PDF</div>
            <div className="flex items-center gap-2">
              <Select value={String(monthN)} onValueChange={(v) => setMonthN(Number(v))}>
                <SelectTrigger className="h-9 w-32" data-testid="att-pdf-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="number" min="2020" max="2099" value={monthYear} onChange={(e) => setMonthYear(Number(e.target.value))} className="h-9 w-28" data-testid="att-pdf-year" />
            </div>
            <div className="text-xs text-muted-foreground">Full calendar month, week-by-week.</div>
            <Button className="w-full h-10" onClick={() => downloadPdf("monthly")} disabled={busy === "monthly"} data-testid="att-pdf-monthly-download">
              <FileDown className="w-4 h-4 mr-2" /> {busy === "monthly" ? "Generating…" : "Download monthly PDF"}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Label className="text-xs">Department filter</Label>
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger className="h-9 w-56" data-testid="att-pdf-dept"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {(departments || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}
